import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, LogOut, Folder, Sparkles, Clock, CheckCircle2, AlertCircle, Coins, TrendingDown, Trash2, Github, BarChart3, Image, Shield, Gift, FlaskConical } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import NotificationBell from './NotificationBell';
import RecentTemplates from './RecentTemplates';

const Sidebar = ({ projects = [], selectedProject, onSelectProject, loadingProjects, onProjectDeleted }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [animateCredits, setAnimateCredits] = useState(false);
  const prevCreditsRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  const displayUser = user || { name: 'User', email: '', plan: 'Free', credits: 0, avatar: '' };
  const credits = displayUser.credits ?? 0;
  const maxCredits = displayUser.plan === 'Pro' ? 500 : displayUser.plan === 'Enterprise' ? 9999 : 50;
  const creditPercent = Math.min(100, Math.max(0, (credits / maxCredits) * 100));

  useEffect(() => {
    if (prevCreditsRef.current !== null && prevCreditsRef.current !== credits) {
      setAnimateCredits(true);
      const timeout = setTimeout(() => setAnimateCredits(false), 600);
      return () => clearTimeout(timeout);
    }
    prevCreditsRef.current = credits;
  }, [credits]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    try {
      await api.delete(`/projects/${projectId}`);
      toast({ title: 'Project deleted' });
      if (onProjectDeleted) onProjectDeleted();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete project', variant: 'destructive' });
    }
  };

  const handleImportGithub = async () => {
    if (!importUrl.trim()) {
      toast({ title: 'Error', description: 'Please enter a GitHub URL', variant: 'destructive' });
      return;
    }
    setImporting(true);
    try {
      const response = await api.post('/projects/import-github', { repo_url: importUrl.trim() });
      if (response.data.success) {
        toast({ title: 'Project Imported!', description: `${response.data.project.name} imported from GitHub.` });
        setShowImportModal(false);
        setImportUrl('');
        if (onProjectDeleted) onProjectDeleted(); // Refresh projects list
      }
    } catch (err) {
      toast({ title: 'Import Failed', description: err.response?.data?.detail || 'Failed to import from GitHub', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'deployed':
        return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case 'building':
        return <Sparkles className="w-4 h-4 text-blue-400 animate-pulse" />;
      case 'testing':
        return <Clock className="w-4 h-4 text-amber-400" />;
      case 'draft':
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
      default:
        return <Folder className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getCreditColor = () => {
    if (creditPercent > 50) return 'bg-emerald-500';
    if (creditPercent > 20) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="w-80 bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-foreground" data-testid="sidebar-title">SuperTool AI</h1>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none" data-testid="user-menu-btn">
                <Avatar className="w-8 h-8 cursor-pointer ring-2 ring-offset-2 ring-offset-[#0f0f0f] ring-transparent hover:ring-blue-500 transition-all">
                  <AvatarImage src={displayUser.avatar || displayUser.picture} />
                  <AvatarFallback className="bg-blue-600 text-white text-sm">
                    {(displayUser.name || 'U').split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-muted border-input">
              <div className="px-2 py-2">
                <p className="text-sm font-medium text-foreground" data-testid="user-name">{displayUser.name}</p>
                <p className="text-xs text-muted-foreground" data-testid="user-email">{displayUser.email}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded" data-testid="user-plan">
                    {displayUser.plan}
                  </span>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-[#222222]" />
              <DropdownMenuItem onClick={() => navigate('/settings')} className="text-foreground/80 focus:bg-accent focus:text-foreground" data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/analytics')} className="text-foreground/80 focus:bg-accent focus:text-foreground" data-testid="analytics-menu-item">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </DropdownMenuItem>
              {displayUser?.email === 'mikeray78lovee@gmail.com' && (
                <DropdownMenuItem onClick={() => navigate('/admin')} className="text-red-300 focus:bg-accent focus:text-red-200" data-testid="admin-menu-item">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} className="text-foreground/80 focus:bg-accent focus:text-foreground" data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        {/* Credit Balance Tracker */}
        <div
          data-testid="credit-balance-tracker"
          className={`mb-4 p-3 rounded-xl border-2 transition-all duration-300 ${
            animateCredits
              ? 'border-amber-500/50 bg-amber-500/10 scale-[1.02]'
              : 'border-border bg-muted'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Coins className={`w-4 h-4 transition-all duration-300 ${animateCredits ? 'text-amber-400 animate-bounce' : 'text-amber-500'}`} />
              <span className="text-xs font-medium text-muted-foreground">Credits</span>
            </div>
            <div className="flex items-center gap-1">
              <span
                data-testid="credit-count"
                className={`text-lg font-bold transition-all duration-300 ${
                  animateCredits ? 'text-amber-400 scale-110' : 'text-white'
                }`}
              >
                {credits}
              </span>
              <span className="text-xs text-muted-foreground">/ {maxCredits}</span>
            </div>
          </div>
          <div className="w-full h-2 bg-accent rounded-full overflow-hidden">
            <div
              data-testid="credit-progress-bar"
              className={`h-full rounded-full transition-all duration-500 ease-out ${getCreditColor()}`}
              style={{ width: `${creditPercent}%` }}
            />
          </div>
          {credits < 10 && (
            <div className="flex items-center gap-1 mt-2" data-testid="low-credits-warning">
              <TrendingDown className="w-3 h-3 text-red-400" />
              <span className="text-xs text-red-400 font-medium">Low credits!</span>
            </div>
          )}
        </div>

        <Button
          data-testid="new-project-btn"
          onClick={() => navigate('/new-project')}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-2 flex items-center justify-center gap-2 transition-all mb-2"
        >
          <Plus className="w-5 h-5" />
          New Project
        </Button>
        
        <Button
          data-testid="templates-btn"
          onClick={() => navigate('/templates')}
          variant="outline"
          className="w-full border-2 border-blue-500/30 hover:bg-blue-500/10 text-blue-400 rounded-lg py-2 flex items-center justify-center gap-2 transition-all"
        >
          <Sparkles className="w-5 h-5" />
          AI Templates
        </Button>

        <Button
          data-testid="compare-models-btn"
          onClick={() => navigate('/compare')}
          variant="outline"
          className="w-full border-2 border-cyan-500/30 hover:bg-cyan-500/10 text-cyan-400 rounded-lg py-2 flex items-center justify-center gap-2 transition-all mt-2"
        >
          <FlaskConical className="w-5 h-5" />
          Compare Models
        </Button>

        <Button
          data-testid="custom-agents-btn"
          onClick={() => navigate('/agents')}
          variant="outline"
          className="w-full border-2 border-purple-500/30 hover:bg-purple-500/10 text-purple-500 rounded-lg py-2 flex items-center justify-center gap-2 transition-all mt-2"
        >
          <FlaskConical className="w-5 h-5" />
          Custom Agents
        </Button>
        
        <Button
          data-testid="import-github-btn"
          onClick={() => setShowImportModal(true)}
          variant="outline"
          className="w-full border-2 border-input hover:bg-secondary text-foreground/80 rounded-lg py-2 flex items-center justify-center gap-2 transition-all mt-2"
        >
          <Github className="w-5 h-5" />
          Import from GitHub
        </Button>

        <Button
          data-testid="promo-kit-btn"
          onClick={() => navigate('/promo')}
          variant="outline"
          className="w-full border-2 border-pink-500/30 hover:bg-pink-500/10 text-pink-400 rounded-lg py-2 flex items-center justify-center gap-2 transition-all mt-2"
        >
          <Image className="w-5 h-5" />
          Promo Kit
        </Button>

        <Button
          data-testid="referral-btn"
          onClick={() => navigate('/referral')}
          variant="outline"
          className="w-full border-2 border-amber-500/30 hover:bg-amber-500/10 text-amber-400 rounded-lg py-2 flex items-center justify-center gap-2 transition-all mt-2"
        >
          <Gift className="w-5 h-5" />
          Invite & Earn
        </Button>
      </div>

      {/* Recently Used Templates */}
      <RecentTemplates />

      {/* Projects List */}
      <div className="flex-1 overflow-y-auto p-4">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Your Projects
        </h2>
        {loadingProjects ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse p-3 rounded-lg bg-muted">
                <div className="h-4 bg-[#222222] rounded w-3/4 mb-2" />
                <div className="h-3 bg-secondary rounded w-full mb-2" />
                <div className="h-3 bg-secondary rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="no-projects-sidebar">
            <Folder className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No projects yet</p>
            <p className="text-xs mt-1 text-muted-foreground/70">Click "New Project" to start</p>
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={`project-item-${project.id}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelectProject(project)}
                onKeyDown={(e) => e.key === 'Enter' && onSelectProject(project)}
                className={`w-full text-left p-3 rounded-lg transition-all group cursor-pointer ${
                  selectedProject?.id === project.id
                    ? 'bg-blue-600/15 border-2 border-blue-500/40'
                    : 'border-2 border-transparent hover:bg-muted'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-foreground text-sm">{project.name}</h3>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(project.status)}
                    <button
                      data-testid={`delete-project-${project.id}`}
                      onClick={(e) => handleDeleteProject(e, project.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/10 rounded"
                      title="Delete project"
                    >
                      <Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                  {project.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {new Date(project.createdAt || project.updatedAt).toLocaleDateString()}
                  </span>
                  <span className="text-xs text-muted-foreground font-medium">
                    {project.credits || 0} credits
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-background">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total Projects</span>
          <span className="font-semibold text-foreground" data-testid="project-count">{projects.length}</span>
        </div>
      </div>

      {/* GitHub Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md bg-muted border-input">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <Github className="w-5 h-5" /> Import from GitHub
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Paste a public GitHub repository URL to import it as a new project.</p>
            <Input
              data-testid="github-import-url-input"
              placeholder="https://github.com/owner/repo"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleImportGithub()}
              className="bg-background border-input text-foreground placeholder:text-muted-foreground"
            />
            <div className="flex gap-2">
              <Button
                data-testid="import-github-submit-btn"
                onClick={handleImportGithub}
                disabled={importing}
                className="flex-1 bg-blue-600 hover:bg-blue-500"
              >
                {importing ? 'Importing...' : 'Import Project'}
              </Button>
              <Button variant="outline" onClick={() => setShowImportModal(false)} className="border-input text-foreground/80 hover:bg-accent">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sidebar;
